import { Controller } from "@hotwired/stimulus";
import { createIcons, icons as lucideIcons } from "lucide";

// Page de paiement Mobile Money — GÉNÉRIQUE (shared/payments/_checkout).
//
// L'acheteur saisit son numéro : l'opérateur se déduit du préfixe (table fournie
// par le serveur, MobilePayments::Msisdn::OPERATOR_PREFIXES — une seule source).
// Le choix manuel reste possible (numéro porté) ; s'il contredit le préfixe, on
// alerte et on demande confirmation — alerter, jamais bloquer.
//
// Tout le chiffrage vient du serveur (simulateUrl → { display: { lines, total,
// notes, payable } }) : ce contrôleur ne calcule que les totaux de ligne.
export default class extends Controller {
  static targets = [
    "form", "cartInput", "operatorInput", "promoInput", "expectedTotalInput",
    "item", "units", "unitsInput", "lineTotal", "lineDetail", "emptyCart", "backLink",
    "promo", "promoFeedback",
    "phone", "phoneWrap", "hint", "badge", "manualToggle", "manual", "option",
    "mismatch", "mismatchText", "confirm",
    "lines", "totalLabel", "totalValue", "notes",
    "cgv", "submit", "submitLabel", "spinner",
  ];

  static values = {
    simulateUrl: String,
    promoUrl: String,
    cart: Object,
    operator: String,
    prefixes: Object,
    labels: Object,
  };

  connect() {
    this.cart = { ...this.cartValue };
    this.manualOperator = this.operatorValue || null;
    this.promoCode = "";
    this.payable = false;
    this.amountLabel = null;
    this.renderCart();
    this.onPhoneInput();
  }

  // ---- Panier -------------------------------------------------------------

  increment(event) {
    this.changeUnits(event.currentTarget.dataset.itemId, +1);
  }

  decrement(event) {
    this.changeUnits(event.currentTarget.dataset.itemId, -1);
  }

  // Ligne à quantité saisie (ex. SMS au détail) : le prix vient du serveur.
  setUnits(event) {
    const id = event.currentTarget.dataset.itemId;
    this.cart[id] = Math.max(0, parseInt(event.currentTarget.value, 10) || 0);
    this.renderCart();
    this.simulate();
  }

  presetUnits(event) {
    const { itemId, units } = event.currentTarget.dataset;
    const input = this.unitsInputTargets.find((el) => el.dataset.itemId === itemId);
    if (input) input.value = units;
    this.cart[itemId] = parseInt(units, 10);
    this.renderCart();
    this.simulate();
  }

  changeUnits(id, delta) {
    this.cart[id] = Math.max(0, (this.cart[id] || 0) + delta);
    this.renderCart();
    this.simulate();
  }

  renderCart() {
    const currency = this.labelsValue.currency || "XAF";
    this.itemTargets.forEach((row) => {
      const id = row.dataset.itemId;
      const units = this.cart[id] || 0;
      const price = parseInt(row.dataset.unitPrice, 10) || 0;
      const unitsEl = this.unitsTargets.find((el) => el.dataset.itemId === id);
      const totalEl = this.lineTotalTargets.find((el) => el.dataset.itemId === id);
      if (unitsEl) unitsEl.textContent = units;
      if (totalEl && row.dataset.serverPriced !== "true") totalEl.textContent = `${this.format(price * units)} ${currency}`;
      row.style.opacity = units ? "1" : ".5";
    });

    const filled = Object.fromEntries(Object.entries(this.cart).filter(([, u]) => u > 0));
    this.cartInputTarget.value = JSON.stringify(filled);
    this.hasItems = Object.keys(filled).length > 0;
    if (this.hasEmptyCartTarget) this.emptyCartTarget.classList.toggle("hidden", this.hasItems);
    if (this.hasBackLinkTarget) this.syncBackLink(filled);
  }

  // « Ajouter un pack » ramène au catalogue sans perdre le panier.
  syncBackLink(filled) {
    const url = new URL(this.backLinkTarget.href, window.location.origin);
    url.searchParams.set("cart", JSON.stringify(filled));
    this.backLinkTarget.href = url.toString();
  }

  // ---- Numéro & opérateur -------------------------------------------------

  onPhoneInput() {
    const national = this.national(this.phoneTarget.value);
    this.detectedOperator = national.length === 9 ? this.detect(national) : null;
    this.phoneValid = /^6\d{8}$/.test(national);
    this.refreshOperator();
  }

  toggleManual() {
    this.manualTarget.classList.toggle("hidden");
    this.manualTarget.classList.toggle("grid", !this.manualTarget.classList.contains("hidden"));
  }

  chooseOperator(event) {
    this.manualOperator = event.currentTarget.dataset.operator;
    if (this.hasConfirmTarget) this.confirmTarget.checked = false;
    this.refreshOperator();
  }

  refreshOperator() {
    const operator = this.manualOperator || this.detectedOperator;
    const operatorChanged = operator !== this.operator;
    this.operator = operator;
    this.operatorInputTarget.value = operator || "";

    this.badgeTargets.forEach((b) => {
      const show = this.phoneValid && b.dataset.operator === operator;
      b.classList.toggle("hidden", !show);
      b.classList.toggle("flex", show);
    });
    this.optionTargets.forEach((o) => {
      o.style.borderColor = o.dataset.operator === this.manualOperator ? "var(--g4s-green)" : "var(--ds-border)";
    });

    const unknown = this.phoneValid && !this.detectedOperator;
    if (unknown && this.manualTarget.classList.contains("hidden")) this.toggleManual();
    this.manualToggleTarget.classList.toggle("hidden", !this.phoneValid || unknown);

    this.mismatch = Boolean(this.phoneValid && this.manualOperator && this.detectedOperator &&
                            this.manualOperator !== this.detectedOperator);
    this.mismatchTarget.classList.toggle("hidden", !this.mismatch);
    if (this.mismatch) {
      this.mismatchTextTarget.textContent = this.mismatchMessage();
    }

    this.renderHint(unknown);
    if (operatorChanged) this.simulate();
    else this.refreshState();
  }

  renderHint(unknown) {
    const l = this.labelsValue;
    const digits = this.national(this.phoneTarget.value);
    let text = l.hint_default;
    let color = "var(--ds-text-3)";
    let invalid = false;
    if (digits.length >= 9 && !this.phoneValid) {
      text = l.hint_invalid;
      invalid = true;
    } else if (unknown && !this.manualOperator) {
      text = l.hint_unknown;
      color = "var(--g4s-warning-fg)";
    } else if (this.phoneValid && this.operator) {
      text = "";
    }
    this.hintTarget.textContent = text;
    this.hintTarget.style.color = invalid ? "var(--g4s-danger-600)" : color;
    this.hintTarget.classList.toggle("hidden", text === "");
    this.phoneWrapTarget.style.borderColor = invalid ? "var(--g4s-danger-600)"
      : (this.phoneValid && this.operator ? "var(--g4s-green)" : "var(--ds-border)");
  }

  mismatchMessage() {
    const names = { mtn: "MTN Mobile Money", orange: "Orange Money" };
    return this.labelsValue.mismatch
      .replace("__DETECTED__", names[this.detectedOperator])
      .replace("__CHOSEN__", names[this.manualOperator]);
  }

  national(raw) {
    let digits = (raw || "").replace(/\D/g, "");
    while (digits.startsWith("237") && digits.length > 9) digits = digits.slice(3);
    return digits;
  }

  detect(national) {
    for (const [operator, prefixes] of Object.entries(this.prefixesValue)) {
      if (prefixes.some((p) => national.startsWith(p))) return operator;
    }
    return null;
  }

  // ---- Code promo ---------------------------------------------------------

  async applyPromo() {
    const code = this.promoTarget.value.trim().toUpperCase();
    if (!code || !this.promoUrlValue) return;
    const data = await this.post(this.promoUrlValue, { cart: this.cartInputTarget.value, code });
    const ok = data && data.valid;
    this.promoCode = ok ? code : "";
    this.promoInputTarget.value = this.promoCode;
    this.promoFeedbackTarget.classList.remove("hidden");
    this.promoFeedbackTarget.style.color = ok ? "var(--g4s-green-700)" : "var(--g4s-danger-600)";
    this.promoFeedbackTarget.textContent = ok
      ? this.labelsValue.promo_applied.replace("__NAME__", data.promo_name)
      : (data && data.error) || this.labelsValue.network_error;
    this.simulate();
  }

  // ---- Récapitulatif (serveur) --------------------------------------------

  simulate() {
    clearTimeout(this.simTimer);
    this.simTimer = setTimeout(() => this.runSimulate(), 200);
  }

  async runSimulate() {
    if (!this.hasItems) {
      this.renderSummary(null);
      return;
    }
    const data = await this.post(this.simulateUrlValue, {
      cart: this.cartInputTarget.value,
      operator: this.operator || "",
      promo_code: this.promoCode,
    });
    // The total shown travels with the order: the server refuses it if the
    // price changed in between (discount, promotion).
    if (this.hasExpectedTotalInputTarget) {
      const total = data && data.display ? data.total_ttc : null;
      this.expectedTotalInputTarget.value = total == null ? "" : String(total);
    }
    this.renderSummary(data && data.display, data && data.error);
  }

  renderSummary(display, error = null) {
    if (!display) {
      this.linesTarget.innerHTML = error
        ? `<p style="font:600 13px var(--g4s-font-sans); color:var(--g4s-danger-600);">${this.escape(error)}</p>`
        : "";
      this.totalValueTarget.textContent = "—";
      this.notesTarget.innerHTML = "";
      this.payable = false;
      this.amountLabel = null;
      this.refreshState();
      return;
    }
    this.linesTarget.innerHTML = display.lines.map((line) => this.lineHtml(line)).join("");
    this.renderServerItems(display.items || []);
    this.totalLabelTarget.textContent = display.total.label;
    this.totalValueTarget.textContent = display.total.value;
    this.notesTarget.innerHTML = (display.notes || []).map((n) => this.noteHtml(n)).join("");
    this.payable = Boolean(display.payable);
    this.amountLabel = display.total.value;
    this.refreshState();
  }

  // Lignes chiffrées par le serveur (prix par tranche) : total et détail.
  renderServerItems(items) {
    items.forEach((item) => {
      const id = String(item.id);
      const totalEl = this.lineTotalTargets.find((el) => el.dataset.itemId === id);
      const detailEl = this.lineDetailTargets.find((el) => el.dataset.itemId === id);
      if (totalEl && item.total) totalEl.textContent = item.total;
      if (detailEl && item.detail) detailEl.textContent = item.detail;
    });
  }

  lineHtml(line) {
    const colors = { success: "var(--g4s-green-700)", muted: "var(--ds-text-3)" };
    const color = colors[line.tone] || "var(--ds-text-1)";
    const detail = line.detail ? ` <span style="color:var(--ds-text-3)">(${this.escape(line.detail)})</span>` : "";
    return `<div class="flex justify-between gap-3" style="font:500 13px var(--g4s-font-sans); color:var(--ds-text-2);">` +
      `<dt>${this.escape(line.label)}${detail}</dt>` +
      `<dd class="tabular-nums" style="font-weight:600; color:${color};">${this.escape(line.value)}</dd></div>`;
  }

  noteHtml(note) {
    return `<li class="flex items-center gap-2" style="font:500 12px var(--g4s-font-sans); color:var(--ds-text-2);">` +
      `<i data-lucide="${this.escape(note.icon)}" class="ds-icon" style="--ds-icon-size:14px"></i>` +
      `<span>${this.escape(note.text)}</span></li>`;
  }

  // ---- Envoi --------------------------------------------------------------

  refreshState() {
    const confirmed = !this.mismatch || (this.hasConfirmTarget && this.confirmTarget.checked);
    const ready = this.hasItems && this.phoneValid && Boolean(this.operator) && confirmed &&
      this.payable && this.hasCgvTarget && this.cgvTarget.checked;
    if (this.hasSubmitTarget) this.submitTarget.disabled = !ready;
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = this.amountLabel && this.payable
        ? this.labelsValue.pay_amount.replace("__AMOUNT__", this.amountLabel)
        : this.labelsValue.pay;
    }
    createIcons({ icons: lucideIcons, attrs: { "stroke-width": 2, "aria-hidden": "true" } });
  }

  submit(event) {
    if (this.submitTarget.disabled) {
      event.preventDefault();
      return;
    }
    this.submitTarget.disabled = true;
    this.submitLabelTarget.classList.add("hidden");
    this.spinnerTarget.classList.remove("hidden");
  }

  // ---- Utilitaires --------------------------------------------------------

  async post(url, body) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
        body: JSON.stringify(body),
      });
      return await res.json();
    } catch (_e) {
      return null;
    }
  }

  format(n) {
    return Math.round(n).toLocaleString("fr-FR").replace(/ | /g, " ");
  }

  escape(value) {
    const div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }
}
